import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import {
  Upload,
  FileText,
  CheckCircle,
  X,
  Download,
  Eye,
  Users,
  AlertTriangle
} from 'lucide-react';

interface BulkUserImportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: any) => void;
}

interface CsvRow {
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  region?: string;
  phone?: string;
  organization?: string;
  notes?: string;
}

interface ImportResult {
  totalRows: number;
  processedRows: number;
  successfulImports: number;
  failedImports: number;
  errors: Array<{
    row: number;
    email: string;
    error: string;
    field?: string;
  }>;
}

export function BulkUserImportDrawer({ isOpen, onClose, onSuccess }: BulkUserImportDrawerProps) {
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [previewRows, setPreviewRows] = useState<CsvRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateCsvRow = (row: CsvRow, rowIndex: number): string[] => {
    const errors: string[] = [];

    // Required fields
    if (!row.email?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Email is required`);
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
      errors.push(`Row ${rowIndex + 1}: Invalid email format`);
    }

    if (!row.first_name?.trim()) {
      errors.push(`Row ${rowIndex + 1}: First name is required`);
    } else if (row.first_name.length < 2 || row.first_name.length > 50) {
      errors.push(`Row ${rowIndex + 1}: First name must be 2-50 characters`);
    } else if (!/^[a-zA-Z\s\-']+$/.test(row.first_name)) {
      errors.push(`Row ${rowIndex + 1}: First name contains invalid characters`);
    }

    if (!row.last_name?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Last name is required`);
    } else if (row.last_name.length < 2 || row.last_name.length > 50) {
      errors.push(`Row ${rowIndex + 1}: Last name must be 2-50 characters`);
    } else if (!/^[a-zA-Z\s\-']+$/.test(row.last_name)) {
      errors.push(`Row ${rowIndex + 1}: Last name contains invalid characters`);
    }

    // Role validation
    if (row.role && !['moderator', 'member', 'media_team', 'pastor'].includes(row.role.toLowerCase())) {
      errors.push(`Row ${rowIndex + 1}: Invalid role. Must be moderator, member, media_team, or pastor`);
    }

    // Phone validation
    if (row.phone && !/^[\+]?[\d\s\-\(\)]{10,}$/.test(row.phone)) {
      errors.push(`Row ${rowIndex + 1}: Invalid phone number format`);
    }

    return errors;
  };

  const parseCsv = (file: File): Promise<CsvRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csv = e.target?.result as string;
          const lines = csv.split('\n').filter(line => line.trim());

          if (lines.length < 2) {
            reject(new Error('CSV file must have at least a header row and one data row'));
            return;
          }

          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

          // Required columns
          const requiredColumns = ['email', 'first_name', 'last_name'];
          const missingColumns = requiredColumns.filter(col => !headers.includes(col));

          if (missingColumns.length > 0) {
            reject(new Error(`Missing required columns: ${missingColumns.join(', ')}`));
            return;
          }

          const data: CsvRow[] = [];
          const errors: string[] = [];

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            if (values.length !== headers.length) continue; // Skip malformed rows

            const row: CsvRow = {
              email: values[headers.indexOf('email')] || '',
              first_name: values[headers.indexOf('first_name')] || '',
              last_name: values[headers.indexOf('last_name')] || '',
              role: values[headers.indexOf('role')] || 'member',
              region: values[headers.indexOf('region')] || '',
              phone: values[headers.indexOf('phone')] || '',
              organization: values[headers.indexOf('organization')] || '',
              notes: values[headers.indexOf('notes')] || '',
            };

            const rowErrors = validateCsvRow(row, i);
            errors.push(...rowErrors);

            data.push(row);
          }

          if (errors.length > 0) {
            setValidationErrors(errors);
          }

          resolve(data);
        } catch (error) {
          reject(new Error('Failed to parse CSV file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('File too large', {
        description: 'Please select a CSV file smaller than 10MB.',
      });
      return;
    }

    setFile(selectedFile);
    setValidationErrors([]);
    setImportResult(null);

    try {
      setIsProcessing(true);
      const data = await parseCsv(selectedFile);
      setCsvData(data);
      setPreviewRows(data.slice(0, 5)); // Show first 5 rows as preview
    } catch (error) {
      toast.error('Failed to process file', {
        description: error instanceof Error ? error.message : 'Unknown error occurred.',
      });
      setFile(null);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    disabled: isProcessing || isImporting,
  });

  const pollImportProgress = async (actionId: string) => {
    try {
      const response = await api.get(`/api/dashboard/actions/status/${actionId}`);

      if (response.success && response.data) {
        const { action, status } = response.data as { action: { result: ImportResult; errorMessage?: string }; status: string };

        if (status === 'completed') {
          setImportResult(action.result);
          toast.success('Import completed', {
            description: `Successfully imported ${action.result.successfulImports} of ${action.result.totalRows} users.`,
          });
          onSuccess(action.result);
          setIsImporting(false);
        } else if (status === 'failed') {
          toast.error('Import failed', {
            description: action.errorMessage || 'An unexpected error occurred.',
          });
          setIsImporting(false);
        } else {
          // Still running, continue polling
          setTimeout(() => pollImportProgress(actionId), 2000);
        }
      }
    } catch (error) {
      console.error('Error polling import progress:', error);
      toast.error('Import monitoring failed');
      setIsImporting(false);
    }
  };

  const handleImport = async () => {
    if (!file || csvData.length === 0) return;

    setIsImporting(true);
    setImportResult(null);

    try {
      // Convert CSV data back to string format for the API
      const csvString = [Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))].join('\n');

      const response = await api.post('/api/dashboard/actions/bulk_user_import', {
        csvData: csvString
      });

      if (response.success && response.data) {
        // For async actions, we get an actionId immediately
        const { actionId, status, message } = response.data as { actionId: string; status: string; message: string };
        toast.success('Import started', {
          description: message,
        });

        // Start polling for progress
        pollImportProgress(actionId);
      } else {
        toast.error('Import failed to start', {
          description: response.error?.message || 'An unexpected error occurred.',
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import failed', {
        description: 'Please check your connection and try again.',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = (nextOpen: boolean = false) => {
    if (!nextOpen && !isImporting && !isProcessing) {
      setFile(null);
      setCsvData([]);
      setPreviewRows([]);
      setValidationErrors([]);
      setImportResult(null);
      onClose();
    }
  };

  const downloadSampleCsv = () => {
    const sampleData = `email,first_name,last_name,role,region,phone,organization
john.doe@dominioncity.org,John,Doe,member,north,555-0101,Pastoral Team
jane.smith@dominioncity.org,Jane,Smith,moderator,south,555-0102,Youth Ministry
media.team@dominioncity.org,Media,Team,media_team,all,555-0103,Production`;

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-users.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFile = () => {
    setFile(null);
    setCsvData([]);
    setPreviewRows([]);
    setValidationErrors([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
        <DrawerHeader>
          <DrawerTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Bulk User Import
          </DrawerTitle>
          <DrawerDescription>
            Upload a CSV file to import multiple users at once. Download the sample file to see the required format.
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 pb-4 space-y-6">
          {/* Sample Download */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Need help? Download a sample CSV file:
            </p>
            <Button variant="outline" size="sm" onClick={downloadSampleCsv}>
              <Download className="h-4 w-4 mr-2" />
              Sample CSV
            </Button>
          </div>

          {/* File Upload */}
          {!file ? (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
                } ${isProcessing || isImporting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <input {...getInputProps()} ref={fileInputRef} />
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              {isDragActive ? (
                <p className="text-lg font-medium">Drop the CSV file here...</p>
              ) : (
                <div>
                  <p className="text-lg font-medium mb-2">
                    {isProcessing ? 'Processing file...' : 'Drop CSV file here or click to browse'}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Only CSV files up to 10MB are supported
                  </p>
                  {isProcessing && <LoadingSpinner size="lg" className="mx-auto" />}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB • {csvData.length} rows
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={clearFile} disabled={isImporting}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium">Found {validationErrors.length} validation errors:</p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {validationErrors.slice(0, 10).map((error, index) => (
                          <p key={index} className="text-sm">{error}</p>
                        ))}
                        {validationErrors.length > 10 && (
                          <p className="text-sm">...and {validationErrors.length - 10} more errors</p>
                        )}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Preview */}
              {previewRows.length > 0 && validationErrors.length === 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    <h4 className="font-medium">Preview (First 5 rows)</h4>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Role</th>
                          <th className="px-3 py-2 text-left">Region</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-3 py-2">{row.email}</td>
                            <td className="px-3 py-2">{`${row.first_name} ${row.last_name}`}</td>
                            <td className="px-3 py-2">
                              <Badge variant="secondary">{row.role || 'member'}</Badge>
                            </td>
                            <td className="px-3 py-2">{row.region || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {csvData.length > 5 && (
                    <p className="text-sm text-muted-foreground text-center">
                      ...and {csvData.length - 5} more rows
                    </p>
                  )}
                </div>
              )}

              {/* Import Progress */}
              {isImporting && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Importing users...</h4>
                    <LoadingSpinner size="sm" />
                  </div>
                  <Progress value={50} className="w-full" />
                  <p className="text-sm text-muted-foreground text-center">
                    Processing user accounts and sending invites...
                  </p>
                </div>
              )}

              {/* Import Results */}
              {importResult && (
                <Alert variant={importResult.failedImports > 0 ? "destructive" : "default"}>
                  {importResult.failedImports > 0 ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium">Import Results:</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>Total: {importResult.totalRows}</div>
                        <div>Successful: {importResult.successfulImports}</div>
                        <div>Failed: {importResult.failedImports}</div>
                        <div>Processed: {importResult.processedRows}</div>
                      </div>
                      {importResult.errors.length > 0 && (
                        <div className="mt-3">
                          <p className="font-medium mb-2">Errors:</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {importResult.errors.slice(0, 5).map((error, index) => (
                              <p key={index} className="text-sm text-destructive">
                                Row {error.row}: {error.error}
                              </p>
                            ))}
                            {importResult.errors.length > 5 && (
                              <p className="text-sm">...and {importResult.errors.length - 5} more errors</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DrawerFooter>
          {file && csvData.length > 0 && validationErrors.length === 0 && !importResult && (
            <Button
              onClick={handleImport}
              disabled={isImporting}
              className="w-full"
            >
              {isImporting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Importing Users...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import {csvData.length} Users
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleClose()}
            disabled={isImporting}
            className="w-full"
          >
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
